import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Space,
  Input,
  Form,
  Breadcrumb,
  Select,
  DatePicker,
  Modal,
} from "antd";
import {
  DoubleRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CountryCode from "CommonComponents/CountryCode";
import {
  AddEmployeeApi,
  EditEmployeeApi,
  getRolesApi,
  GetEmployeeByIdApi,
} from "../EmployeesAPI";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import OpenNotificationWithIcon from "CommonComponents/ResultNotification/Notification";
import { resetEmployeeDetailsById, setCurrentPage } from "../EmployeesSlice";
import CanadaCities from "features/Enums/CanadaCities";
import EmployeeSkills from "features/Enums/EmployeeSkills";

function AddEmployee(props) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = props?.employeeData?.id || routeId;
  const intl = useIntl();
  const selectedLanguage = localStorage.getItem("selectedlanguage");
  const companyId = localStorage.getItem("CompanyId");
  const rolesRecords = useSelector((state) => state.employees.rolesRecords);
  const employeeDetails = useSelector(
    (state) => state.employees.employeeRecordById
  );
  const input = {
    pageNo: 1,
    pageSize: 10,
    searchQuery: "",
  };
  const companyNames = useSelector((state) => state.lookup.companyNames);
  useEffect(() => {
    dispatch(getRolesApi());
    if (id > 0) {
      dispatch(GetEmployeeByIdApi(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && employeeDetails) {
      form.setFieldsValue({
        ...employeeDetails,
        birthDate: employeeDetails?.birthDate
          ? dayjs(employeeDetails?.birthDate)
          : null,
        gender: employeeDetails?.gender,
        employeeSkill: employeeDetails?.employeeSkill
          ? employeeDetails.employeeSkill.split(/\s*,\s*/)
          : [],
        isActive: employeeDetails?.isActive ?? true,
      });
    }
  }, [employeeDetails, form, id]);

  const disableFutureDate = (current) =>
    current && current.isAfter(dayjs().endOf("day"));
  const handleSubmit = async (values) => {
    const formData = {
      ...values,
      birthDate: values?.birthDate
        ? dayjs(values?.birthDate).format("YYYY-MM-DD")
        : null,
      employeeSkill: values?.employeeSkill?.join(", ") || "",
      mappedCompany: companyId,
      isActive: id ? values?.isActive : true,
    };

    try {
      if (id) {
        dispatch(EditEmployeeApi({ ...formData, id }, props?.filter));
      } else {
        dispatch(AddEmployeeApi(formData, input));
        dispatch(setCurrentPage());
        form.resetFields();
        dispatch(resetEmployeeDetailsById());
      }
      //navigate("/admin");
      if (props?.onClose) {
        props.onClose();
      }
    } catch (error) {
      console.error("Error during form submission:", error);
    }
  };
  const handleCancel = () => {
    const hasValidationErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);
    if (form.isFieldsTouched() || hasValidationErrors) {
      Modal.confirm({
        title: "Are you sure you want to cancel?",
        okText: intl.formatMessage({ id: "Common.Yes" }),
        cancelText: intl.formatMessage({ id: "Common.No" }),
        onOk: () => {
          form.resetFields();
          props?.onClose();
        },
      });
    } else {
      props?.onClose();
    }
  };
  return (
    <Form
      form={form}
      name="basic"
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        //countryCode: "+1",
        isActive: true,
      }}
      autoComplete="off"
      size="large"
    >
      <Row gutter={16}>
        {/* First Name.. */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.FirstName" />}
            name="firstName"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Firstnameisrequired",
                }),
              },
              {
                max: 50,
                message: intl.formatMessage({
                  id: "Employee.Firstnamelimit",
                }),
              },
              {
                validator: (_, value) => {
                  if (value && value !== value.trim()) {
                    return Promise.reject(
                      intl.formatMessage({
                        id: "Employee.NoLeadingTrailingSpaces",
                      })
                    );
                  }
                  return Promise.resolve();
                },
              },
              {
                pattern: /^[A-Za-z\s]+$/,
                message: intl.formatMessage({
                  id: "Employee.Onlylettersareallowed",
                }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: "Employee.Enterfirstname",
              })}
              maxLength={50}
            />
          </Form.Item>
        </Col>

        {/* Last Name */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.LastName" />}
            name="lastName"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Lastnameisrequired",
                }),
              },
              {
                max: 50,
                message: intl.formatMessage({
                  id: "Employee.Lastnamelimit",
                }),
              },
              {
                validator: (_, value) => {
                  if (value && value !== value.trim()) {
                    return Promise.reject(
                      intl.formatMessage({
                        id: "Employee.NoLeadingTrailingSpaces",
                      })
                    );
                  }
                  return Promise.resolve();
                },
              },
              {
                pattern: /^[A-Za-z\s]+$/,
                message: intl.formatMessage({
                  id: "Employee.Onlylettersareallowed",
                }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: "Employee.Enterlastname",
              })}
              maxLength={50}
            />
          </Form.Item>
        </Col>

        {/* Date of Birth */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.DateofBirth" />}
            name="birthDate"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Dateofbirthisrequired",
                }),
              },
            ]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              disabledDate={disableFutureDate}
              placeholder={intl.formatMessage({
                id: "Employee.Selectdate",
              })}
            />
          </Form.Item>
        </Col>

        {/* Employee ID */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.EmployeeId" />}
            name="employeeId"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.EmployeeIDisrequired",
                }),
              },
              {
                pattern: /^[A-Za-z0-9]+$/,
                message: intl.formatMessage({
                  id: "Employee.Onlylettersnumbersallowed",
                }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: "Employee.EnteremployeeID",
              })}
              maxLength={50}
            />
          </Form.Item>
        </Col>

        {/* Phone number */}
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.Phonenumber" />}
            name="phone"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Pleaseenterphonenumber",
                }),
              },
              {
                pattern: /^[0-9]{10,11}$/,
                message: intl.formatMessage({
                  id: "AddClient.error.Phonenumbermustbe10digits",
                }),
              },
            ]}
          >
            <Input
              addonBefore={
                <CountryCode /> ? <CountryCode name="countryCode" /> : ""
              }
              style={{
                width: "100%",
              }}
              maxLength={11}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              onPaste={(event) => {
                const pasteData = event.clipboardData.getData("Text");
                if (!/^\d+$/.test(pasteData)) {
                  event.preventDefault();
                }
              }}
              placeholder={intl.formatMessage({
                id: "Employee.Enterphonenumber",
              })}
            />
          </Form.Item>
        </Col>

        {/* Email */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.Email" />}
            name="email"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Emailisrequired",
                }),
              },
              {
                type: "email",
                message: intl.formatMessage({
                  id: "Employee.Enteravalidemail",
                }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: "Employee.Enteremail",
              })}
            />
          </Form.Item>
        </Col>

        {/* Gender */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.Gender" />}
            name="gender"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Genderisrequired",
                }),
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={intl.formatMessage({
                id: "Employee.Selectgender",
              })}
            >
              <Select.Option
                value="male"
                label={intl.formatMessage({ id: "Employee.Male" })}
              >
                <FormattedMessage id="Employee.Male" />
              </Select.Option>
              <Select.Option
                value="female"
                label={intl.formatMessage({ id: "Employee.Female" })}
              >
                <FormattedMessage id="Employee.Female" />
              </Select.Option>
              <Select.Option
                value="other"
                label={intl.formatMessage({ id: "Employee.Other" })}
              >
                <FormattedMessage id="Employee.Other" />
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>

        {/* Access Role */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.AccessRole" />}
            name="roleId"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "Employee.Accessroleisrequired",
                }),
              },
            ]}
          >
            <Select
              showSearch
              placeholder={intl.formatMessage({
                id: "Employee.Selectrole",
              })}
              allowClear
              optionFilterProp="children"
            >
              {rolesRecords?.map((role) => (
                <Select.Option key={role?.id} value={role?.id}>
                  {selectedLanguage === "fr-FR" ? role.nameFr : role.nameEn}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Skills */}
        <Col xs={24} sm={24}>
          <Form.Item
            label={<FormattedMessage id="Employee.Skills" />}
            name="employeeSkill"
          >
            <Select
              mode="multiple"
              showSearch
              placeholder={intl.formatMessage({
                id: "Employee.SelectSkills",
              })}
              allowClear
              optionFilterProp="children"
            >
              {EmployeeSkills.map((skill) => (
                <Select.Option key={skill} value={skill}>
                  {skill}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Location */}
        <Col xs={24} sm={12}>
          <Form.Item
            label={<FormattedMessage id="Employee.Location" />}
            name="location"
          >
            <Select
              showSearch
              placeholder={intl.formatMessage({
                id: "Employee.SelectLocation",
              })}
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .includes(
                    input
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                  )
              }
            >
              {Object.entries(CanadaCities).map(([province, cities]) =>
                cities.map((city) => (
                  <Select.Option key={`${province}-${city}`} value={city}>
                    {city}
                  </Select.Option>
                ))
              )}
            </Select>
          </Form.Item>
        </Col>

        {/* Status */}
        <Col xs={24} sm={12}>
          {id && (
            <Form.Item
              label={<FormattedMessage id="Employee.Status" />}
              name="isActive"
              initialValue={true}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Employee.Statusisrequired",
                  }),
                },
              ]}
            >
              <Select
                placeholder={intl.formatMessage({
                  id: "Employee.Selectstatus",
                })}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const text = intl.formatMessage({
                    id: option.value ? "Common.Active" : "Common.InActive",
                  });
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
              >
                <Select.Option value={true}>
                  {<FormattedMessage id="Common.Active" />}
                </Select.Option>
                <Select.Option value={false}>
                  {<FormattedMessage id="Common.InActive" />}
                </Select.Option>
              </Select>
            </Form.Item>
          )}
        </Col>

        {/* Buttons */}
        <Col span={24} className="text-center mt-10">
          <Space>
            <Button danger size="medium" onClick={handleCancel}>
              <CloseCircleOutlined />
              <FormattedMessage id="Common.Cancel" />
            </Button>
            <Button type="primary" size="medium" htmlType="submit">
              <CheckCircleOutlined />
              {id ? (
                <FormattedMessage id="Employee.UpdateEmployee" />
              ) : (
                <FormattedMessage id="Employee.AddEmployee" />
              )}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
}

export default AddEmployee;
